/**
 * Historical Volatility (HV)
 *
 * Annualized standard deviation of log returns over `period` bars.
 * Expressed as a percentage.
 *
 * Category: Volatility | Pane: Separate
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";

export const historicalVolIndicator: IndicatorDefinition = {
  id: "historicalvol",
  name: "Historical Volatility",
  shortName: "HV",
  category: "volatility",
  pane: "separate",
  params: [
    { key: "period", label: "Period", type: "number", default: 20, min: 2, max: 500 },
    { key: "annualize", label: "Annualize Factor", type: "number", default: 252, min: 1, max: 365 },
    { key: "color", label: "Color", type: "color", default: "#795548" },
  ],
  plots: [
    { key: "hv", label: "HV", color: "#795548", style: "line", lineWidth: 2, priceScaleId: "hv" },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { period, annualize = 252 } = params;
    const data: { time: number; value: number }[] = [];

    if (candles.length < period + 1) return [{ key: "hv", data }];

    // Log returns
    const logReturns: number[] = [];
    for (let i = 1; i < candles.length; i++) {
      logReturns.push(candles[i - 1].close > 0 ? Math.log(candles[i].close / candles[i - 1].close) : 0);
    }

    for (let i = period - 1; i < logReturns.length; i++) {
      let sum = 0;
      for (let j = i - period + 1; j <= i; j++) sum += logReturns[j];
      const mean = sum / period;

      let sqSum = 0;
      for (let j = i - period + 1; j <= i; j++) sqSum += (logReturns[j] - mean) ** 2;

      const stdDev = Math.sqrt(sqSum / (period - 1));
      const hv = stdDev * Math.sqrt(annualize) * 100;
      data.push({ time: candles[i + 1].time, value: hv });
    }

    return [{ key: "hv", data }];
  },
};
